import { HTTP_METHODS } from 'constants/general';
import {
  AI_CHAT_BY_ID, AI_CHAT_DATASOURCE_BY_ID, AI_CHAT_MESSAGES_BY_CHAT, AI_CHATS,
} from 'constants/requestUrls';
import mainApi from 'store/apis/mainApi';
import AI_CHATS_LIST_TAG, { AI_CHAT_MESSAGES_TAG } from 'store/tags/aiChats';

import type IBaseQueryResponse from 'store/types/IBaseQueryResponse';
import type IAiChat from '../interfaces/IAiChat';
import type IAiChatDatasourcePage from '../interfaces/IAiChatDatasourcePage';
import type IAiChatDatasourcePageRequest from '../interfaces/IAiChatDatasourcePageRequest';
import type IAiChatMessage from '../interfaces/IAiChatMessage';
import type IAiChatMessagesRequest from '../interfaces/IAiChatMessagesRequest';

/** The server answers a delete of a chat that is already gone with this, and that is a success. */
const ALREADY_GONE_STATUS = 403;

/**
 * The AI chats API, apart from sending a message.
 *
 * Sending is deliberately absent: that endpoint answers with a streamed UI message stream, which
 * `fetchBaseQuery` can only hand back once it has ended. `useAiChatConversation` reads it through
 * the SDK's own transport instead.
 *
 * The chat list is serialized and comes in the `{ data }` envelope. The messages and datasource
 * controllers are not, so they answer with the bare body.
 *
 * Routes and response shapes:
 * @see [5. API](../../../../../docs/mockups/ai-agents/ai-chat/ATT-144_AI_Chat_Assistant.md)
 */
export const aiChatsApi = mainApi.injectEndpoints({
  endpoints: (builder) => ({
    getAiChats: builder.query<IAiChat[], void>({
      query: () => ({
        url: AI_CHATS,
      }),
      transformResponse: (response: IBaseQueryResponse<IAiChat[]>) => response.data,
      providesTags: [AI_CHATS_LIST_TAG],
    }),

    getAiChatMessages: builder.query<IAiChatMessage[], IAiChatMessagesRequest>({
      query: ({
        chatId, limit, cursorId, order,
      }) => ({
        url: AI_CHAT_MESSAGES_BY_CHAT.replace('{chatId}', `${chatId}`),
        params: {
          limit,
          ...(cursorId === undefined ? {} : { cursorId }),
          ...(order ? { order } : {}),
        },
      }),
      providesTags: (_result, _error, { chatId }) => [{ type: AI_CHAT_MESSAGES_TAG, id: chatId }],
    }),

    // A datasource never changes once stored, so its pages carry no tag: nothing invalidates them.
    getAiChatDatasourcePage: builder.query<IAiChatDatasourcePage, IAiChatDatasourcePageRequest>({
      query: ({ datasourceId, offset, limit }) => ({
        url: AI_CHAT_DATASOURCE_BY_ID.replace('{datasourceId}', `${datasourceId}`),
        params: { offset, limit },
      }),
    }),

    deleteAiChat: builder.mutation<void, number>({
      // The server does not tell a chat that was never the caller's from one already deleted, so
      // a 403 means the chat is gone either way — which is what the delete was for.
      async queryFn(chatId, _api, _extraOptions, baseQuery) {
        const { error } = await baseQuery({
          url: AI_CHAT_BY_ID.replace('{chatId}', `${chatId}`),
          method: HTTP_METHODS.DELETE,
        });

        return error && error.status !== ALREADY_GONE_STATUS ? { error } : { data: undefined };
      },
      invalidatesTags: [AI_CHATS_LIST_TAG],
    }),
  }),
});

export const {
  useGetAiChatsQuery,
  useLazyGetAiChatsQuery,
  useGetAiChatMessagesQuery,
  useGetAiChatDatasourcePageQuery,
  useDeleteAiChatMutation,
} = aiChatsApi;

export default aiChatsApi;
